/**
 * Agent Store - Persistent agent state manager
 *
 * This module provides a simple global store to maintain agent state across
 * component unmounts and re-renders. It uses a singleton pattern to ensure
 * there's only one source of truth for agent data throughout the application.
 */
import React from 'react';

// Store implementation using a simple singleton pattern
class AgentStore {
  constructor() {
    // Initialize state
    this.agents = [];
    this.loading = true;
    this.error = null;
    this.listeners = new Set();
    this.processedMessageIds = new Set();
    this.lastCleanupTime = Date.now();
    this.statusCheckInterval = null;

    // Start status check interval
    this.startStatusCheck();
  }

  // Subscribe to store changes
  subscribe(listener) {
    this.listeners.add(listener);
    return () => {
      this.listeners.delete(listener);
    };
  }

  // Notify all subscribers of state changes
  notifyListeners() {
    this.listeners.forEach(listener => {
      listener(this.getState());
    });
  }

  // Get current state
  getState() {
    return {
      agents: [...this.agents],
      loading: this.loading,
      error: this.error,
    };
  }

  // Set loading state
  setLoading(isLoading) {
    this.loading = isLoading;
    this.notifyListeners();
  }

  // Set error state
  setError(error) {
    this.error = error;
    this.loading = false;
    this.notifyListeners();
  }

  // Clear error state
  resetError() {
    this.error = null;
    this.notifyListeners();
  }

  // Process a message from MQTT
  processMessage(message) {
    try {
      // Skip if no ID in message
      const agentId = message.id || message.agent_id;
      if (!agentId) {
        console.warn('Received agent message without ID:', message);
        return;
      }

      // Generate a deterministic message ID for deduplication
      const messageId = `${agentId}-${JSON.stringify(message)}`;

      // Skip if we've already processed this exact message recently
      if (this.processedMessageIds.has(messageId)) {
        console.log('Skipping duplicate message for agent:', agentId);
        return;
      }

      // Add to processed set
      this.processedMessageIds.add(messageId);

      // Clean up the processed message cache periodically
      const now = Date.now();
      if (now - this.lastCleanupTime > 60000) {
        // Cleanup every minute
        console.log('Cleaning processed message cache in agentStore');
        this.processedMessageIds.clear();
        this.lastCleanupTime = now;
      }

      // Force status check after processing a message to catch any agents that haven't sent updates
      // Use setTimeout to ensure this doesn't block message processing
      setTimeout(() => this.checkAgentStatus(), 0);

      // Find existing agent or create new entry
      const existingAgentIndex = this.agents.findIndex(agent => agent.id === agentId);

      // Important: Always trust the current status from message
      // Status message is definitive (online/offline)
      const currentTimestamp = new Date().toISOString();

      if (existingAgentIndex >= 0) {
        // Update existing agent
        const updatedAgent = {
          ...this.agents[existingAgentIndex],
          ...message,
          lastSeen: currentTimestamp,
        };

        // Handle forced offline status (from verification)
        if (message.forceOffline === true) {
          console.log(`Force offline flag detected for agent ${agentId}`);
          updatedAgent.status = 'offline';
          // Don't update lastSeen when forcing offline - this preserves the staleness data
          if (this.agents[existingAgentIndex].lastSeen) {
            updatedAgent.lastSeen = this.agents[existingAgentIndex].lastSeen;
          }
        }

        // Store original timestamp from message separately for stale detection
        if (message.timestamp) {
          console.log(`Agent ${agentId} original message timestamp: ${message.timestamp}`);
          updatedAgent.messageTimestamp = message.timestamp;
        }

        // Ensure status is set explicitly based on the message
        if (message.status) {
          console.log(`Explicitly setting agent ${agentId} status to ${message.status}`);
        }

        // Create new array with updated agent
        const newAgents = [...this.agents];
        newAgents[existingAgentIndex] = updatedAgent;
        this.agents = newAgents;

        console.log(
          `Updated agent ${agentId} in persistent store with status: ${updatedAgent.status}`
        );
      } else {
        // Add new agent
        const newAgent = {
          ...message,
          lastSeen: currentTimestamp,
        };

        // Store original timestamp from message separately for stale detection
        if (message.timestamp) {
          console.log(`Agent ${agentId} original message timestamp: ${message.timestamp}`);
          newAgent.messageTimestamp = message.timestamp;
        }

        // Ensure new agent has a status
        if (!newAgent.status) {
          newAgent.status = 'unknown';
        }

        this.agents = [...this.agents, newAgent];
        console.log(
          `Added new agent ${agentId} to persistent store with status: ${newAgent.status}`
        );
      }

      // Mark as no longer loading once we have agent data
      if (this.loading) {
        this.loading = false;
      }

      // Notify all subscribers
      this.notifyListeners();
    } catch (error) {
      console.error('Error processing agent message in store:', error);
      this.setError(error);
    }
  }

  // Check agent status and mark stale ones as offline
  checkAgentStatus(immediate = false) {
    const now = Date.now();
    let updated = false;

    // More aggressive: Consider agents stale after 30 seconds without updates
    const staleThreshold = 30 * 1000; // 30 seconds

    const updatedAgents = this.agents.map(agent => {
      // Skip if already offline
      if (agent.status === 'offline') {
        return agent;
      }

      // First check if agent has a timestamp in the message
      const messageTimestamp = agent.timestamp;
      if (messageTimestamp) {
        try {
          // Parse the agent's timestamp from message
          const agentTime = new Date(messageTimestamp).getTime();
          const ageInSeconds = Math.round((now - agentTime) / 1000);

          // If timestamp is older than 5 minutes, mark offline
          if (ageInSeconds > 300) {
            updated = true;
            return {
              ...agent,
              status: 'offline',
            };
          }
        } catch (err) {
          console.error(`[AGENT] Error parsing timestamp for ${agent.id}:`, err.message);
        }
      }

      // Fallback to lastSeen (when we received the message) for stale detection
      if (!agent.lastSeen) {
        updated = true;
        return {
          ...agent,
          status: 'offline',
          lastSeen: new Date().toISOString(),
        };
      }

      const lastSeenTime = new Date(agent.lastSeen).getTime();
      const lastSeenAgeSeconds = Math.round((now - lastSeenTime) / 1000);

      if (lastSeenAgeSeconds > staleThreshold) {
        updated = true;
        return {
          ...agent,
          status: 'offline',
        };
      }

      return agent;
    });

    if (updated) {
      this.agents = updatedAgents;
      this.notifyListeners();
    }

    return updated;
  }

  // Start status check interval to mark stale agents as offline
  startStatusCheck() {
    // Clear any existing interval
    if (this.statusCheckInterval) {
      clearInterval(this.statusCheckInterval);
    }

    // Immediately check status when starting
    this.checkAgentStatus(true);

    // Check every 15 seconds for stale agents (more frequent checking)
    this.statusCheckInterval = setInterval(() => {
      this.checkAgentStatus();
    }, 15000); // Check every 15 seconds
  }

  // Manually force an offline status for all agents
  forceAllAgentsOffline() {
    if (this.agents.length === 0) {
      return;
    }

    console.log('Forcing all agents offline');
    this.agents = this.agents.map(agent => ({
      ...agent,
      status: 'offline',
    }));

    this.notifyListeners();
  }

  // Reset the store
  reset() {
    this.agents = [];
    this.loading = true;
    this.error = null;
    this.processedMessageIds.clear();

    // Clear status check interval
    if (this.statusCheckInterval) {
      clearInterval(this.statusCheckInterval);
      this.statusCheckInterval = null;
    }

    this.notifyListeners();

    // Restart the status check
    this.startStatusCheck();
  }
}

// Create singleton instance
const agentStore = new AgentStore();

// Hook for components to use the store
export function useAgentStore() {
  const [state, setState] = React.useState(agentStore.getState());

  React.useEffect(() => {
    const unsubscribe = agentStore.subscribe(newState => {
      setState(newState);
    });

    return unsubscribe;
  }, []);

  return {
    ...state,
    processMessage: message => agentStore.processMessage(message),
    setLoading: isLoading => agentStore.setLoading(isLoading),
    resetError: () => agentStore.resetError(),
    reset: () => agentStore.reset(),
  };
}

export default agentStore;
